import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

/**
 * Profile photos sit behind an authenticated endpoint, so they cannot be a plain
 * <img src>. They are fetched as a blob and handed around as an object URL.
 *
 * Three avatars are mounted at once — sidebar, mobile header, bottom nav — and
 * the responsive layout only hides the ones it does not need, so all three
 * hooks run. Each used to request its own width, which produced three requests
 * per page load, two of them for a byte-identical URL, and three Pillow resizes
 * on the server. Remounting the shell doubled that again.
 *
 * So the request is shared rather than repeated: one width for every call site,
 * and one in-flight promise per user and photo version that concurrent callers
 * await instead of starting their own.
 */

/** One size for every call site. 128 covers the largest avatar (36px) at 2x. */
const PHOTO_WIDTH = 128;

/** key -> Promise<objectUrl>. Holds at most one entry per user. */
const photoCache = new Map();

function photoKey(userId, version) {
  return `${userId}:${version}`;
}

function loadPhoto(api, userId, version) {
  const key = photoKey(userId, version);
  const cached = photoCache.get(key);
  if (cached) return cached;

  const promise = api
    .get(`/users/${userId}/photo`, {
      params: { w: PHOTO_WIDTH, v: version },
      responseType: 'blob',
    })
    .then((res) => URL.createObjectURL(res.data))
    .catch((error) => {
      // A failed fetch must not be cached, or the avatar stays broken until reload.
      photoCache.delete(key);
      throw error;
    });

  // A new version supersedes the old one; release its bitmap.
  for (const [existing, pending] of photoCache) {
    if (existing !== key && existing.startsWith(`${userId}:`)) {
      photoCache.delete(existing);
      pending.then(URL.revokeObjectURL).catch(() => {});
    }
  }

  photoCache.set(key, promise);
  return promise;
}

/**
 * De foto van wie dan ook, mits je hem mag zien.
 *
 * Deelt dezelfde cache en dezelfde breedte als de eigen avatar, dus twee
 * plekken die om dezelfde collega vragen doen samen een verzoek. De server
 * bewaakt wie wat mag: niet je eigen foto en geen admin betekent dat jullie
 * allebei in het leaderboard moeten staan.
 */
export function useProfilePhotoUrlFor({ userId, version = 0, hasPhoto }) {
  const { axiosAuth } = useAuth();
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!hasPhoto || !userId) {
      setUrl(null);
      return undefined;
    }

    let cancelled = false;
    loadPhoto(axiosAuth(), userId, version)
      .then((objectUrl) => {
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => {
        // Mag niet of bestaat niet: dan blijven de initialen staan.
        if (!cancelled) setUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [hasPhoto, userId, version, axiosAuth]);

  return url;
}

export function useProfilePhotoUrl() {
  const { axiosAuth, user } = useAuth();
  const [url, setUrl] = useState(null);

  const userId = user?.id;
  const version = user?.profile_photo_updated_at ?? 0;
  const hasPhoto = user?.has_profile_photo;

  useEffect(() => {
    if (!hasPhoto || !userId) {
      setUrl(null);
      return undefined;
    }

    let cancelled = false;
    loadPhoto(axiosAuth(), userId, version)
      .then((objectUrl) => {
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });

    // The object URL stays alive in the cache for the other avatars, so
    // unmounting here revokes nothing.
    return () => {
      cancelled = true;
    };
  }, [hasPhoto, userId, version, axiosAuth]);

  return url;
}

export function initialsOf(name) {
  return (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export default function UserAvatar({ size = 36, showStatus = false, className = '' }) {
  const { user } = useAuth();
  const photoUrl = useProfilePhotoUrl();

  return (
    <span className={`relative inline-flex flex-shrink-0 ${className}`} style={{ width: size, height: size }}>
      <span
        className="w-full h-full rounded-full overflow-hidden bg-dd-s3 border border-dd-line flex items-center justify-center"
        aria-hidden={photoUrl ? undefined : true}
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-dd-muted font-semibold" style={{ fontSize: Math.max(10, size * 0.36) }}>
            {initialsOf(user?.name)}
          </span>
        )}
      </span>
      {showStatus && (
        <span
          className="absolute bottom-0 right-0 rounded-full bg-dd-lime border-2 border-dd-s1"
          style={{ width: size * 0.28, height: size * 0.28 }}
        />
      )}
    </span>
  );
}
